import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBusinessUpgradeRequests, reviewBusinessUpgrade } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const DOC_FIELDS = [
  { key: "cacCertificateUrl", label: "CAC Certificate" },
  { key: "cacStatusReportUrl", label: "CAC Status Report" },
  { key: "directorIdUrl", label: "Director ID" },
  { key: "taxCertificateUrl", label: "Tax Certificate" },
  { key: "proofOfAddressUrl", label: "Proof of Address (Tier 3)" },
  { key: "idDocumentUrl", label: "ID Document (Tier 2)" },
];

export default function BusinessUpgradeRequests() {
  const dispatch = useDispatch();
  const requests = useSelector((s) => s.profile.businessUpgradeRequests || []);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { dispatch(getBusinessUpgradeRequests(status)); }, [dispatch, status]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const handleApprove = async (userId) => {
    setBusyId(userId);
    const res = await dispatch(reviewBusinessUpgrade(userId, "approve"));
    setBusyId(null);
    if (res?.success) dispatch(getBusinessUpgradeRequests(status));
    else alert(res?.message || "Unable to approve application");
  };

  const startReject = (userId) => { setRejectingId(userId); setReason(""); };
  const cancelReject = () => { setRejectingId(null); setReason(""); };

  const confirmReject = async (userId) => {
    if (!reason.trim()) return alert("Please provide a rejection reason");
    setBusyId(userId);
    const res = await dispatch(reviewBusinessUpgrade(userId, "reject", reason.trim()));
    setBusyId(null);
    if (res?.success) {
      setRejectingId(null);
      setReason("");
      dispatch(getBusinessUpgradeRequests(status));
    } else alert(res?.message || "Unable to reject application");
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>SeerBit Business Upgrade Requests</h2>
          <p style={S.sub}>Review and approve merchant applications for a business (KYB) wallet account</p>
        </div>
      </div>

      <div style={S.tabs}>
        {["pending", "approved", "rejected"].map((t) => (
          <button key={t} style={{ ...S.tab, ...(status === t ? S.tabActive : {}) }} onClick={() => setStatus(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.list}>
        {requests.length === 0 ? (
          <div style={S.empty}>No {status} business applications</div>
        ) : requests.map((r) => {
          const userId = r.userId?._id;
          const isExpanded = expandedId === userId;
          return (
            <div key={r._id} style={S.card}>
              <div style={S.cardHeader} onClick={() => setExpandedId(isExpanded ? null : userId)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: BG, fontSize: 14 }}>{r.business?.businessName || "—"}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {r.userId?.storeInfo?.storeName || "—"} · {r.userId?.email || "—"}
                  </div>
                </div>
                <span style={{ ...S.statusPill, ...statusStyle(r.business?.status) }}>{r.business?.status}</span>
                <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`} style={{ color: "#888", marginLeft: 12 }} />
              </div>

              {isExpanded && (
                <div style={S.cardBody}>
                  <div style={S.fieldsGrid}>
                    <Field label="RC Number" value={r.business?.rcNumber} />
                    <Field label="TIN" value={r.business?.tin} />
                    <Field label="Tax ID" value={r.business?.taxId} />
                    <Field label="Business Address" value={r.business?.businessAddress} />
                    <Field label="Business Phone" value={r.business?.businessPhone} />
                    <Field label="Business Email" value={r.business?.businessEmail} />
                    <Field label="Director Name" value={r.business?.directorName} />
                    <Field label="Director BVN" value={r.business?.directorBvn} />
                    <Field label="Director NIN" value={r.business?.directorNin} />
                    <Field label="Submitted" value={fmtDate(r.business?.submittedAt)} />
                  </div>

                  <div style={{ fontSize: 12, fontWeight: 700, color: BG, marginTop: 16, marginBottom: 8 }}>Documents</div>
                  <div style={S.docsRow}>
                    {DOC_FIELDS.map((d) => {
                      const url = r.business?.documents?.[d.key];
                      return url ? (
                        <a key={d.key} href={url} target="_blank" rel="noreferrer" style={S.docLink}>
                          <i className="bi bi-file-earmark-text" style={{ marginRight: 4 }} />{d.label}
                        </a>
                      ) : (
                        <span key={d.key} style={S.docMissing}>{d.label} — not provided</span>
                      );
                    })}
                  </div>

                  {r.business?.status === "rejected" && r.business?.rejectionReason && (
                    <div style={S.rejectionNotice}>
                      <strong>Rejection reason:</strong> {r.business.rejectionReason}
                    </div>
                  )}

                  {status === "pending" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                      <button style={S.approveBtn} disabled={busyId === userId} onClick={() => handleApprove(userId)}>
                        <i className="bi bi-check-lg" style={{ marginRight: 4 }} />Approve
                      </button>
                      <button style={S.rejectBtn} disabled={busyId === userId} onClick={() => startReject(userId)}>
                        <i className="bi bi-x-lg" style={{ marginRight: 4 }} />Reject
                      </button>
                    </div>
                  )}

                  {rejectingId === userId && (
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                      <input
                        style={S.reasonInput}
                        placeholder="Reason for rejection (sent to the merchant by email)…"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        autoFocus
                      />
                      <button style={S.rejectBtn} disabled={busyId === userId} onClick={() => confirmReject(userId)}>Confirm Reject</button>
                      <button style={S.cancelBtn} onClick={cancelReject}>Cancel</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: "#999" }}>{label}</div>
    <div style={{ fontSize: 13, color: BG, fontWeight: 500 }}>{value || "—"}</div>
  </div>
);

const statusStyle = (status) => {
  if (status === "approved") return { background: "rgba(167,204,72,0.12)", color: "#5a8a00" };
  if (status === "rejected") return { background: "rgba(230,57,70,0.1)", color: "#e63946" };
  return { background: "rgba(244,162,97,0.15)", color: "#c1741f" };
};

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  tabs: { display: "flex", gap: 6, marginBottom: 18 },
  tab: { padding: "7px 14px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#888" },
  tabActive: { background: PRIMARY, borderColor: PRIMARY, color: "#fff" },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  empty: { background: "#fff", borderRadius: 10, padding: 32, textAlign: "center", color: "#bbb", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  card: { background: "#fff", borderRadius: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" },
  cardHeader: { display: "flex", alignItems: "center", padding: "14px 18px", cursor: "pointer" },
  cardBody: { padding: "0 18px 18px", borderTop: "1px solid #f5f5f5", paddingTop: 16 },
  fieldsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 },
  docsRow: { display: "flex", gap: 14, flexWrap: "wrap" },
  docLink: { color: "#4fb0c6", fontSize: 12, fontWeight: 600, textDecoration: "none" },
  docMissing: { color: "#bbb", fontSize: 12 },
  statusPill: { borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 700 },
  rejectionNotice: { marginTop: 14, padding: "10px 14px", background: "rgba(230,57,70,0.06)", borderRadius: 8, fontSize: 12, color: "#e63946" },
  approveBtn: { background: "rgba(167,204,72,0.12)", border: "1px solid " + PRIMARY, borderRadius: 6, color: "#5a8a00", padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  rejectBtn: { background: "rgba(230,57,70,0.08)", border: "1px solid #e63946", borderRadius: 6, color: "#e63946", padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  cancelBtn: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, color: "#888", padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  reasonInput: { flex: 1, minWidth: 260, padding: "7px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none" },
};
